import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { CHANNEL_SHORT_NAME_MAP, IChannel, PROTOCOL } from '@/entities/channel';
import { useAuthorisationVk } from '@/features/channels/model/useAuthorisationVk';
import { Stack, Typography } from '@/shared/ui';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { Link } from '@/shared/ui/lib/Link';
import { ControlledTextField } from '@/shared/ui/TextField';

import { createVkFormDataSchema } from '../../model/schemas';
import { ChannelIconWithLabel } from '../ChannelIconWithLabel';

const FORM_ID = 'create-vk-form';
const VK_TOKEN_HELP_URL = 'https://dev.vk.com/ru/api/access-token/community-token';
const VK_GROUPS_URL = 'https://vk.com/groups?act=admin';

interface IVkAuthDialogProps extends TDialogBase {
  onCreated?: (channel: IChannel) => void;
  channelId: string;
}

export const VkAuthDialog = ({
  open,
  onClose,
  onCreated,
  channelId,
}: IVkAuthDialogProps) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset: resetFormValues,
  } = useForm({
    resolver: zodResolver(createVkFormDataSchema(t)),
    defaultValues: { token: '', groupId: '' },
    mode: 'onChange',
  });

  const { authorizeVk, isPending } = useAuthorisationVk();

  const handleCreate = async (channelData: IChannel) => {
    resetFormValues();
    onCreated?.(channelData);
  };

  const onSubmit = handleSubmit(async (data) => {
    await authorizeVk(
      channelId,
      { token: data.token, group_id: data.groupId },
      handleCreate
    );
  });

  const handleClose = () => {
    onClose?.();
    resetFormValues();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('channels:vk.authTitle')}
      additionalTitleSlot={
        <ChannelIconWithLabel
          label={CHANNEL_SHORT_NAME_MAP[PROTOCOL.VK]}
          protocol={PROTOCOL.VK}
        />
      }
      leftButtonSlot={
        <Button variant="outlined" onClick={handleClose}>
          {t('channels:buttons.closeModal')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={isPending}
        >
          {t('channels:vk.connect')}
        </Button>
      }
    >
      <form onSubmit={onSubmit} id={FORM_ID}>
        <Stack spacing={2}>
          <ControlledTextField
            name="token"
            control={control}
            autoFocus
            error={!!errors.token}
            label={t('channels:vk.token')}
          />
          <Typography variant="body2">
            <Link href={VK_TOKEN_HELP_URL} target="_blank" rel="noopener noreferrer">
              {t('channels:vk.tokenLink')}
            </Link>
          </Typography>
          <ControlledTextField
            name="groupId"
            control={control}
            error={!!errors.groupId}
            label={t('channels:vk.groupId')}
          />
          <Typography variant="body2">
            <Link href={VK_GROUPS_URL} target="_blank" rel="noopener noreferrer">
              {t('channels:vk.groupIdLink')}
            </Link>
          </Typography>
        </Stack>
      </form>
    </Dialog>
  );
};
